import { Fragment } from "react";
import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { PageShell, Card } from "@/components/page-shell";
import { CheckArea } from "@/components/zip-checker";
import {
  ArticleBody,
  DotList,
  EndNote,
  ReviewedDate,
  Section,
  SourcesAndDisclaimer,
} from "@/components/guide-page";
import { slugifyHeading, type TocItem } from "@/components/toc";
import { ShareRow } from "@/components/share-row";
import { ARTICLES, type Article } from "@/lib/articles";
import { articleJsonLd, breadcrumbJsonLd } from "@/lib/jsonld";
import { absUrl } from "@/lib/site";
import { pageTitle } from "@/lib/seo";

export const Route = createFileRoute("/resources/$slug")({
  // The content module is imported dynamically so it lands in this route's own chunk
  // instead of the shared entry chunk that every page downloads. routeTree.gen.ts
  // imports each route's loader and head eagerly, so a top-level import here would
  // put this file's prose on the home page's critical path.
  loader: async ({ params }): Promise<{ article: Article }> => {
    const { getArticle } = await import("@/lib/articles");
    const article = getArticle(params.slug);
    if (!article) {
      // Articles folded into a guide on the same question go to that guide.
      const { resolveRedirect } = await import("@/lib/redirects");
      const target = resolveRedirect(`/resources/${params.slug}`);
      if (target) throw redirect({ href: target, statusCode: 301 });
      throw notFound();
    }
    return { article };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Article not found | LoneStarMap" }],
      };
    }
    const a = loaderData.article;
    return {
      meta: [
        { title: pageTitle(a.metaTitle ?? a.title) },
        { name: "description", content: a.description },
        { name: "keywords", content: a.keywords.join(", ") },
        { property: "og:title", content: a.title },
        { property: "og:description", content: a.description },
        { property: "og:url", content: absUrl(`/resources/${params.slug}`) },
        { property: "og:type", content: "article" },
        { property: "article:published_time", content: a.updated },
        { property: "article:modified_time", content: a.updated },
        { property: "article:section", content: a.category },
      ],
      links: [{ rel: "canonical", href: absUrl(`/resources/${params.slug}`) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            articleJsonLd({
              headline: a.title,
              description: a.description,
              path: `/resources/${params.slug}`,
              datePublished: a.updated,
              section: a.category,
              keywords: a.keywords,
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Resources", path: "/resources" },
              { name: a.title, path: `/resources/${params.slug}` },
            ]),
          ),
        },
      ],
    };
  },
  errorComponent: ({ error }) => (
    <PageShell title="Couldn't load article">
      <p className="text-sm text-muted-foreground">
        {error instanceof Error ? error.message : String(error)}
      </p>

    </PageShell>
  ),
  notFoundComponent: () => (
    <PageShell title="Article not found">
      <p className="text-sm text-muted-foreground">
        We don't have that article. Browse all{" "}
        <Link to="/resources" className="link font-semibold">
          resources
        </Link>
        .
      </p>
    </PageShell>
  ),
  component: ArticlePage,
});

function ArticlePage() {
  const { article: a } = Route.useLoaderData() as { article: import("@/lib/articles").Article };
  const related = ARTICLES.filter((x) => x.slug !== a.slug && x.category === a.category).slice(
    0,
    3,
  );
  const more = ARTICLES.filter((x) => x.slug !== a.slug && x.category !== a.category).slice(0, 3);
  const toc: TocItem[] = [
    ...a.sections.map((s) => ({ id: slugifyHeading(s.h), label: s.h })),
    { id: "key-takeaways", label: "Key takeaways" },
  ];

  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Resources", path: "/resources" },
        { name: a.title, path: `/resources/${a.slug}` },
      ]}
      eyebrow={a.category}
      title={a.title}
      intro={a.description}
    >
      <ReviewedDate iso={a.updated} label="Updated" extra={<> · {a.readMinutes} min read</>} />

      <ArticleBody toc={toc}>
        <p className="measure text-[1.1875rem] leading-[1.65] text-foreground">{a.intro}</p>

        {a.sections.map((s, i) => (
          <Fragment key={s.h}>
            <Section id={slugifyHeading(s.h)} title={s.h}>
              <DotList items={s.points} />
            </Section>
            {/* One stop partway down for the reader who came here to check their own area. */}
            {i === 0 && (
              <Card>
                <h2 className="font-display text-xl font-medium text-foreground">
                  Are they where you live?
                </h2>
                <p className="measure mt-1 text-[0.9375rem] leading-relaxed text-muted-foreground">
                  Enter a ZIP code, city or state to see what&rsquo;s known about Lone Star ticks
                  there.
                </p>
                <div className="mt-4">
                  <CheckArea compact />
                </div>
              </Card>
            )}
          </Fragment>
        ))}

        <Section id="key-takeaways" title="Key takeaways">
          <DotList items={a.takeaways} />
        </Section>


        <aside aria-labelledby="share-article" className="no-print">
          <h2 id="share-article" className="label-caps text-muted-foreground">
            Share this article
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Send it to someone before they head outside.
          </p>
          <div className="mt-3">
            <ShareRow
              path={`/resources/${a.slug}`}
              text={`${a.title}, a free guide from LoneStarMap:`}
            />
          </div>
        </aside>

        {related.length > 0 && (
          <ArticleIndex title={`More in ${a.category}`} id="more-in" items={related} />
        )}

        <div>
          <ArticleIndex title="Other tick & alpha-gal guides" id="other-guides" items={more} />
          <Link to="/resources" className="link mt-4 inline-block text-sm font-semibold">
            All articles
          </Link>
        </div>

        <EndNote>
          This article is here to help you learn. It is not medical advice. If you have symptoms or
          questions, see your doctor or an allergist. If you&rsquo;re having a severe reaction, call
          911. <SourcesAndDisclaimer />
        </EndNote>
      </ArticleBody>
    </PageShell>
  );
}

/** A short ruled index of articles: topic and read time over the title. */
function ArticleIndex({ title, id, items }: { title: string; id: string; items: Article[] }) {
  return (
    <nav aria-labelledby={id}>
      <h2 id={id} className="label-caps text-muted-foreground">
        {title}
      </h2>
      <ul className="mt-2 divide-y divide-border border-y border-border">
        {items.map((r) => (
          <li key={r.slug} className="py-3">
            <p className="tabular text-xs text-muted-foreground">
              {r.category} · {r.readMinutes} min
            </p>
            <Link
              to="/resources/$slug"
              params={{ slug: r.slug }}
              className="mt-0.5 block font-display text-lg leading-snug text-foreground transition-colors hover:text-primary"
            >
              {r.title}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
